import { useEffect, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Badge } from "../../components/ui/badge";
import { Plus, Trash2, FileText, Check, X, BookOpen, ExternalLink } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";
import { refsForSection } from "./referenceLibrary";

const TABS = [
  { id: "audit", label: "Audit & Accounting" },
  { id: "mca", label: "MCA / Company Law" },
  { id: "income_tax", label: "Income Tax" },
  { id: "gst", label: "GST" },
  { id: "rbi_fema", label: "RBI & FEMA" },
  { id: "sebi", label: "SEBI" },
  { id: "valuation", label: "Valuation" },
  { id: "insolvency", label: "IBC" },
  { id: "judicial", label: "Judicial" },
  { id: "others", label: "Others" },
];

export default function KipDocuments() {
  const { user } = useAuth();
  const [tab, setTab] = useState("audit");
  const [status, setStatus] = useState("");
  const [docs, setDocs] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const canApprove = ["partner", "admin"].includes(user?.role);

  const load = async () => {
    const params = { tab };
    if (status) params.status = status;
    const { data } = await api.get("/kip/documents", { params });
    setDocs(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab, status]);

  const approve = async (id) => { await api.post(`/kip/documents/${id}/approve`); toast.success("Approved"); load(); };
  const reject = async (id) => { await api.post(`/kip/documents/${id}/reject`); toast.success("Rejected"); load(); };
  const del = async (id) => { if (!window.confirm("Delete?")) return; await api.delete(`/kip/documents/${id}`); load(); };

  return (
    <div className="space-y-6" data-testid="kip-docs-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="eyebrow">Knowledge Intelligence</div>
          <h1 className="font-sans text-3xl">Knowledge Repository</h1>
        </div>
        <Button onClick={() => setShowNew(true)} data-testid="upload-doc-btn"><Plus className="h-4 w-4 mr-1"/>Upload</Button>
      </div>

      <div className="flex gap-2 flex-wrap">
        {TABS.map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`px-3 py-1.5 rounded-full text-xs border ${tab === t.id ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200 hover:border-slate-400"}`}>
            {t.label}
          </button>
        ))}
      </div>
      {canApprove && (
        <Select value={status || "_all"} onValueChange={(v) => setStatus(v === "_all" ? "" : v)}>
          <SelectTrigger className="w-48"><SelectValue/></SelectTrigger>
          <SelectContent>
            <SelectItem value="_all">All status</SelectItem>
            <SelectItem value="pending">Pending Approval</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
          </SelectContent>
        </Select>
      )}

      <div className="grid gap-3">
        {docs.map((d) => (
          <Card key={d.id} className="p-4" data-testid={`doc-${d.id}`}>
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <FileText className="h-4 w-4 text-slate-500"/>
                  <span className="font-semibold text-slate-900">{d.title}</span>
                  {d.status === "pending" && <Badge className="bg-amber-100 text-amber-700">Pending</Badge>}
                  {d.status === "approved" && <Badge className="bg-emerald-100 text-emerald-700">Approved</Badge>}
                  {d.status === "rejected" && <Badge className="bg-red-100 text-red-700">Rejected</Badge>}
                </div>
                {d.category && <div className="text-xs text-slate-500">Category: {d.category}</div>}
                {d.description && <p className="text-sm text-slate-700 mt-1">{d.description}</p>}
                <div className="text-xs text-slate-500 mt-2">Uploaded by {d.uploaded_by_name} · {d.created_at?.slice(0, 10)}</div>
                {d.file_url && <a href={d.file_url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline">Download {d.file_name}</a>}
              </div>
              <div className="flex gap-1">
                {canApprove && d.status === "pending" && (<>
                  <Button size="sm" variant="ghost" onClick={() => approve(d.id)} data-testid={`approve-${d.id}`}><Check className="h-3 w-3 text-emerald-700"/></Button>
                  <Button size="sm" variant="ghost" onClick={() => reject(d.id)}><X className="h-3 w-3 text-red-700"/></Button>
                </>)}
                <Button size="sm" variant="ghost" onClick={() => del(d.id)}><Trash2 className="h-3 w-3"/></Button>
              </div>
            </div>
          </Card>
        ))}
        {!docs.length && <Card className="p-8 text-center text-slate-400">No documents</Card>}
      </div>

      <SectionDocsRefs section={tab}/>

      {showNew && <NewDocDialog tab={tab} onClose={() => setShowNew(false)} onSaved={() => { setShowNew(false); load(); }}/>}
    </div>
  );
}

function SectionDocsRefs({ section }) {
  const refs = refsForSection(section);
  if (!refs.length) return null;
  return (
    <Card className="p-4 bg-amber-50/40 border-amber-200" data-testid="docs-section-refs">
      <div className="flex items-center gap-2 mb-3">
        <BookOpen className="h-4 w-4 text-[#C2A671]"/>
        <h2 className="font-semibold text-slate-900">Reference Library for this Section</h2>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
        {refs.map((r) => (
          <a key={r.id} href={r.url} target="_blank" rel="noopener noreferrer"
            className="border border-amber-200 rounded p-2 text-sm bg-white hover:border-amber-400">
            <div className="font-medium text-slate-900 text-xs line-clamp-2 leading-tight">{r.label}</div>
            <div className="text-[10px] text-slate-500 mt-1 inline-flex items-center gap-1">{r.source} <ExternalLink className="h-3 w-3"/></div>
          </a>
        ))}
      </div>
    </Card>
  );
}

function NewDocDialog({ tab, onClose, onSaved }) {
  const [form, setForm] = useState({ tab, title: "", category: "", description: "", file_url: "", file_name: "" });
  const save = async () => {
    if (!form.title.trim()) { toast.error("Title required"); return; }
    try { await api.post("/kip/documents", form); toast.success("Uploaded"); onSaved(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl" data-testid="new-doc-dialog">
        <DialogHeader><DialogTitle>Upload Document</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><label className="text-xs text-slate-500">Tab</label>
            <Select value={form.tab} onValueChange={(v) => setForm({...form, tab: v})}>
              <SelectTrigger><SelectValue/></SelectTrigger>
              <SelectContent>{TABS.map(t => <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><label className="text-xs text-slate-500">Title *</label><Input value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} data-testid="doc-title"/></div>
          <div><label className="text-xs text-slate-500">Category</label><Input value={form.category} onChange={(e) => setForm({...form, category: e.target.value})} placeholder="e.g. Standards / SOP / Checklist"/></div>
          <div><label className="text-xs text-slate-500">Description</label><Textarea value={form.description} onChange={(e) => setForm({...form, description: e.target.value})}/></div>
          <div><label className="text-xs text-slate-500">External File URL (Google Drive, etc.)</label><Input value={form.file_url} onChange={(e) => setForm({...form, file_url: e.target.value})}/></div>
          <div><label className="text-xs text-slate-500">File Name</label><Input value={form.file_name} onChange={(e) => setForm({...form, file_name: e.target.value})}/></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button onClick={save} data-testid="doc-save">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
